import type { Project, Phase, Item, Subtask } from "./types";
import { parentOf } from "./hierarchy";
import { parseDate } from "./format";

export const IMPORT_COLUMNS = [
  "Phase", "Phase description", "Phase start", "Phase end",
  "Item", "Type", "Status", "Priority", "Start date", "Due date", "Recur",
  "Client visible", "Depends on", "Part of", "Waiting on", "Checklist", "Notes",
];

const MS_DAY = 86_400_000;

const STATUS_LABEL: Record<string, string> = {
  todo: "Not started", doing: "In progress", waiting: "Waiting", blocked: "Blocked", done: "Done",
};

function csvCell(v: string): string {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Build a reusable template CSV from a project. Dates are written as day offsets
 * (e.g. +0, +5) relative to an anchor — the project's start date, or the earliest
 * date in the plan. Re-importing lets you pick a new start date and every date
 * recalculates from it.
 *
 * Relationships travel as titles, not ids: dependencies via "Depends on" and the
 * §7 parent link via "Part of". Without the latter a template round-trip
 * silently flattened every deliverable's tasks back into the phase.
 */
export function buildTemplateCsv(project: Project, phases: Phase[], items: Item[], subtasks: Subtask[]): string {
  // Anchor = project start, else earliest date found anywhere in the plan.
  const times: number[] = [];
  const push = (s?: string) => { const d = parseDate(s); if (d) times.push(d.getTime()); };
  phases.forEach((p) => { push(p.start); push(p.end); });
  items.forEach((i) => { push(i.start); push(i.due); });
  const anchor = parseDate(project.start)?.getTime() ?? (times.length ? Math.min(...times) : Date.now());

  const off = (s?: string): string => {
    const d = parseDate(s);
    if (!d) return "";
    const n = Math.round((d.getTime() - anchor) / MS_DAY);
    return n >= 0 ? `+${n}` : `${n}`;
  };

  const titleById = new Map(items.map((i) => [i.id, i.title]));
  const itemsById = new Map(items.map((i) => [i.id, i]));
  const subsByItem = new Map<string, Subtask[]>();
  for (const s of subtasks) {
    if (!subsByItem.has(s.item)) subsByItem.set(s.item, []);
    subsByItem.get(s.item)!.push(s);
  }

  const orderedPhases = [...phases].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const lines = [IMPORT_COLUMNS.join(",")];

  for (const ph of orderedPhases) {
    const phItems = items
      .filter((i) => i.phase === ph.id)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    for (const it of phItems) {
      const checklist = (subsByItem.get(it.id) || [])
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((s) => s.title)
        .join("; ");
      const dependsOn = it.dependsOn ? (titleById.get(it.dependsOn) || "") : "";
      // parentOf() resolves stale links and the pre-v5 `milestone` field, so the
      // template carries what the app actually shows.
      const partOf = parentOf(it, itemsById)?.title || "";
      lines.push([
        ph.name, ph.description || "", off(ph.start), off(ph.end),
        it.title, it.type, STATUS_LABEL[it.status] || it.status, it.priority,
        off(it.start), off(it.due), it.recur || "none",
        it.clientVisible ? "Yes" : "No", dependsOn, partOf, it.waitOn || "",
        checklist, it.notes || "",
      ].map(csvCell).join(","));
    }
  }

  return lines.join("\n");
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
